module Sm.Service.Repository

open System
open FsToolkit.ErrorHandling
open Browser.Types
open Fable.Core

open Sm.Core.Interop

open Sm.Service.App
open Sm.Service.Database
open Sm.Service.Tracking
open Sm.Service.Storage

[<Import("runInAction", "mobx")>]
let internal runInAction (f: unit -> unit) = jsNative<unit>

let JournalCode = Code.demand 1

type Adjustment = {
    Id: Guid
    Field: string
    Value: string option
}

type Update = {
    Clock: int64
    Client: Guid
    Target: Adjustment
}

type MergeEvent = 
    {
        Fields: Map<string, Document.Register>
        Update: Update
    }
    member this.Id = this.Update.Target.Id

type Limit<'T> =
    | Inclusive of 'T
    | Exclusive of 'T

module Limit =
    let unwrap = function
        | Inclusive x -> x, false
        | Exclusive x -> x, true

    let map f = function
        | Inclusive x -> Inclusive (f x)
        | Exclusive x -> Exclusive (f x)

type DateRange = 
    | Exact of DateOnly
    | Start of Limit<DateOnly>
    | End of Limit<DateOnly>
    | Between of Limit<DateOnly> * Limit<DateOnly>
    static member Min = Inclusive DateOnly.MinValue
    static member Max = Inclusive DateOnly.MaxValue
    static member Unbounded = Between (DateRange.Min, DateRange.Max)

module DateRange =
    let normalise = function
        | Exact date -> (Inclusive date), (Inclusive date)
        | Start lower -> lower, DateRange.Max
        | End upper -> DateRange.Min, upper
        | Between (lower, upper) -> lower, upper

    let map f range =
        range
        |> normalise
        |> fun (l, h) -> (f l), (f h)

    let contains date range =
        let after = function
            | Inclusive d -> date >= d
            | Exclusive d -> date > d

        let before = function
            | Inclusive d -> date <= d
            | Exclusive d -> date < d

        match range with
        | Exact d -> date = d
        | Start lower -> after lower
        | End upper -> before upper
        | Between (lower, upper) -> after lower && before upper

type LocalBatch = {
    BatchCount: int
    TotalCount: int
    Clock: int64
    Updates: Update list
}

type Repository(env) =
    let mutable clock = VectorClock.create ()
    let merged = Event<MergeEvent>()

    let criticalScope = CriticalScope(nameof Repository)

    let sync theirs =
        let nextLocal = LogicalClock.sync clock.Local theirs
        clock <- { clock with Local = nextLocal }

    let tick () =
        let now = LogicalClock.now ()
        sync now
        clock.Local

    let keyRange code dateRange =
        let format date =
            Lookup.format { Code = code; Date = date }

        let prepare limit =
            limit
            |> Limit.map format
            |> Limit.unwrap

        let (lower, lowerOpen), (upper, upperOpen) = dateRange |> DateRange.map prepare
        IDBKeyRange.bound(lower, upper, lowerOpen, upperOpen)

    member _.Merged = merged.Publish
    member _.Clock = clock

    member _.Start() =
        criticalScope.Promise() {
            let! x = VectorClock.load env
            clock <- x
        }

    member _.Prepare(adjustments) =
        adjustments
        |> Seq.map (fun target -> 
            { Clock = tick (); Target = target; Client = env.ClientId }
        )

    member _.Merge(updates) =
        let byId =
            updates
            |> Seq.groupBy (fun x -> x.Target.Id)

        let mergeEvents = ResizeArray<_>()

        criticalScope.Promise() {
            let! db = connect env.DatabaseName
            let tx = db.transaction([| RecordStore; TrackingStore|], Readwrite)
            
            let recordStore = tx.objectStore(RecordStore)
            let trackingStore = tx.objectStore(TrackingStore)

            for id, updates in byId do
                let! current = recordStore.get(id) |> result

                let fields = 
                    current
                    |> Option.bind (Document.parse env.ClientId)
                    |> Option.map (fun x -> x.Fields)
                    |> Option.defaultValue Map.empty

                let tracking = ResizeArray<_>()

                let fields =
                    (fields, updates)
                    ||> Seq.fold (fun fields update ->
                        sync update.Clock

                        let fields, applied =
                            fields
                            |> Document.merge update.Target.Field {
                                Clock = update.Clock
                                Value = update.Target.Value 
                                Client = update.Client
                            }

                        if applied then
                            match update.Target.Field, update.Target.Value with
                            | Document.TrackerField, Some value ->
                                value 
                                |> Data.parse
                                |> tracking.AddRange
                            | _ -> ()

                            mergeEvents.Add(
                                { Fields = fields; Update = update }
                            )

                        fields
                    )

                if tracking.Count > 0 then
                    let byDate =
                        tracking
                        |> Seq.groupBy (fun x ->
                            let range, _ = Data.Record.parse x
                            range.Date
                        )

                    for date, items in byDate do
                        let key = Lookup.formatDate date
                        let! data = trackingStore |> Data.load key

                        let nextData =
                            data
                            |> Seq.append items
                            |> Seq.distinct

                        do! trackingStore |> Data.save key nextData

                let next = Document.serialize env.ClientId { Id = id; Fields = fields }
                do! recordStore.put(next) |> result<unit>
        }
        |> Promise.catch(fun _ ->  
            mergeEvents.Clear()
        )
        |> Promise.map (fun _ ->
            runInAction(fun () ->
                for e in mergeEvents do
                    merged.Trigger(e)
            )
        )

    member _.UpdateClocks(values) =
        criticalScope.Promise() {
            clock <- clock |> VectorClock.update values
            do! env |> VectorClock.save clock
        }

    member _.QueryRecord(id: Guid) =
        criticalScope.Promise() {
            let! rs = env.DatabaseName |> readRecordStore
            let! item = rs.get(id) |> result<obj option>

            return 
                item 
                |> Option.bind (Document.parse env.ClientId)
        }

    member _.QueryLocal(since, count) =
        criticalScope.Promise() {
            let! rs = env.DatabaseName |> writeRecordStore
            let local = rs.index("local")

            let range = IDBKeyRange.lowerBound(Number.create(since), true)
            let! total = local.count(range) |> result<int>

            let! keys =
                local.openKeyCursor(range, Next)
                |> Cursor.take count (fun c -> 
                    match c.primaryKey with
                    | Some (JsString s) -> Some s
                    | _ -> None
                )
                |> Promise.map (fun keys ->
                    keys
                    |> Seq.choose id
                    |> Seq.distinct
                )

            let! items =
                keys
                |> Seq.map (fun id -> rs.get(id) |> result<obj option>)
                |> Promise.all

            let mutable clock = 0L

            let updates =
                items
                |> Seq.choose (Document.parse env.ClientId)
                |> Seq.collect (fun d -> seq {
                    for KeyValue(name, r) in d.Fields do
                        d.Id, name, r
                })
                |> Seq.filter (fun (_, _, r) -> r.Client = env.ClientId && r.Clock > since)
                |> Seq.sortBy (fun (_, _, r) -> r.Clock)
                |> Seq.truncate count
                |> Seq.map (fun (id, name, register) ->
                    clock <- max clock register.Clock
                    {
                        Clock = register.Clock
                        Client = register.Client
                        Target = {
                            Id = id
                            Field = name
                            Value = register.Value
                        }
                    }
                )
                |> List.ofSeq

            return {
                TotalCount = total
                BatchCount = count
                Clock = clock
                Updates = updates
            }
        }

    member _.QueryJournal(date) =
        criticalScope.Promise() {
            let! rs = env.DatabaseName |> readRecordStore
            let index = rs.index("activity")

            let range = keyRange (Some JournalCode) (Exact date)
            let! items = index.getAll(range) |> result<obj array>

            return items
            |> Seq.map (Document.parse env.ClientId)
            |> Seq.choose id
            |> List.ofSeq
        }

    member _.QueryActivity(code) =
        criticalScope.Promise() {
            let! rs = env.DatabaseName |> readRecordStore
            let! count = rs.count() |> result<int>

            //safari: if you try to open a key cursor with nextunique direction
            //on an empty store, the operation will fail, thankfully this
            //workaround is quite rational in it's own right.
            if count = 0 then
                return Set.empty
            else
                let index = rs.index("activity")

                let range = keyRange (Some code) DateRange.Unbounded
                let request = index.openKeyCursor(range, Nextunique)
                let! items = request |> Cursor.scan (fun _ c -> Some (string c.key))

                return items
                |> Seq.choose Lookup.parse
                |> Seq.map _.Date
                |> Set.ofSeq
        }

    member _.QueryTracking(range) =
        criticalScope.Promise() {
            let! store = env.DatabaseName |> readStore TrackingStore

            let range = keyRange None range
            let! items = store.getAll(range) |> result<Data.Array64 array>

            let data =
                items
                |> Seq.collect Data.unpack
                |> Data.decode

            return Map.empty |> Store.apply data
        }

    member _.QueryPendingEntries() =
        criticalScope.Promise() {
            let! rs = env.DatabaseName |> readRecordStore
            let index = rs.index("pending")

            let range = keyRange (Some JournalCode) DateRange.Unbounded
            let! items = index.getAll(range) |> result<obj array>

            return items
            |> Seq.map (Document.parse env.ClientId)
            |> Seq.choose id
            |> List.ofSeq
        }
